import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  ContestBudgetRange,
  ContestBudgetRangesCollection,
  ContestFee,
  ContestFeesCollection,
  ContestMinimumBudgetClassification,
  ContestUpgradeCardOption,
  CurrenciesCollection,
  Currency,
} from '@freelancer/datastore/collections';
import { SelectItem } from '@freelancer/ui/select';
import * as Rx from 'rxjs';
import { StepName } from '../form.component';
import { DurationType } from './duration-type/duration-type.component';

@Component({
  selector: 'app-contest',
  template: `
    <app-contest-budget
      *ngIf="showContestBudget"
      flTrackingSection="PJPContestBudget"
      id="{{ StepName.CONTEST_BUDGET }}"
      [budgetRange$]="budgetRange$"
      [budgetRangeStatus$]="budgetRangeStatus$"
      [contestBudgetControl]="formGroup.get(StepName.CONTEST_BUDGET)"
      [contestMinimumBudgetClassification$]="
        contestMinimumBudgetClassification$
      "
      [currenciesStatus$]="currenciesStatus$"
      [currencyIdControl]="formGroup.get('currencyId')"
      [currencyOptions$]="currencyOptions$"
      [selectedCurrency$]="selectedCurrency$"
    ></app-contest-budget>
    <app-contest-duration-type
      id="{{ StepName.CONTEST_DURATION_TYPE }}"
      flTrackingSection="PJPContestDuration"
      [control]="formGroup.get(StepName.CONTEST_DURATION_TYPE)"
      [hidden]="!showDurationType"
      [selectedCurrency]="selectedCurrency$ | async"
      [upgradeFees]="upgradeFees$ | async"
      [upgradeFeesStatus]="upgradeFeesStatus$ | async"
      (cardSelected)="handleContestDurationTypeCardSelected($event)"
    ></app-contest-duration-type>
    <app-contest-duration
      *ngIf="showContestDuration"
      flTrackingSection="PJPContestDuration"
      id="{{ StepName.CONTEST_DURATION }}"
      [control]="formGroup.get(StepName.CONTEST_DURATION)"
    ></app-contest-duration>
    <app-contest-upgrades
      flTrackingSection="PJPContestUpgrades"
      id="{{ StepName.CONTEST_UPGRADES }}"
      [contestTypeControl]="formGroup.get('contestType')"
      [control]="formGroup.get(StepName.CONTEST_UPGRADES)"
      [hidden]="!showContestUpgrades"
      [upgradeFees]="upgradeFees$ | async"
      [upgradeFeesStatus]="upgradeFeesStatus$ | async"
      [selectedCurrency]="selectedCurrency$ | async"
      (advancedUpgradesClicked)="advancedUpgradesClicked.emit($event)"
      (cardSelected)="handleContestUpgradeCardSelected($event)"
    ></app-contest-upgrades>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestComponent {
  StepName = StepName;

  @Input() contestMinimumBudgetClassification$: Rx.Observable<
    ContestMinimumBudgetClassification
  >;
  @Input() budgetRange$: Rx.Observable<ContestBudgetRange>;
  @Input() budgetRangeStatus$: Rx.Observable<
    RequestStatus<ContestBudgetRangesCollection>
  >;
  @Input() currenciesStatus$: Rx.Observable<
    RequestStatus<CurrenciesCollection>
  >;
  @Input() currencyOptions$: Rx.Observable<ReadonlyArray<SelectItem>>;
  @Input() formGroup: FormGroup;
  @Input() selectedCurrency$: Rx.Observable<Currency>;
  @Input() showContestBudget: boolean;
  @Input() showDurationType: boolean;
  @Input() showContestDuration: boolean;
  @Input() showContestUpgrades: boolean;
  @Input() upgradeFees$: Rx.Observable<ContestFee>;
  @Input() upgradeFeesStatus$: Rx.Observable<
    RequestStatus<ContestFeesCollection>
  >;

  @Output()
  cardSelected: EventEmitter<{
    stepName: StepName;
    value: DurationType | ContestUpgradeCardOption;
  }> = new EventEmitter();
  @Output() advancedUpgradesClicked: EventEmitter<boolean> = new EventEmitter();

  handleContestDurationTypeCardSelected(value: DurationType) {
    const urgent = value === DurationType.URGENT;
    this.formGroup.controls[StepName.CONTEST_UPGRADES].patchValue({ urgent });

    this.cardSelected.emit({ stepName: StepName.CONTEST_DURATION_TYPE, value });
  }

  handleContestUpgradeCardSelected(value: ContestUpgradeCardOption) {
    this.cardSelected.emit({ stepName: StepName.CONTEST_UPGRADES, value });
  }
}
